/**
 * Pure eligibility rules for the admin lane map. Mirrors the production
 * behaviour in hitch-services:
 *
 *   - station candidacy: StationService.getStationsAlongRoute
 *   - shared door radius: UserQuoteService.resolveWithinSharedDoorRadius
 *
 * Distances arrive precomputed from the server (PostGIS ::geography), so these
 * functions are plain comparisons — no geometry maths here, and no dependency
 * on React or the map.
 *
 * The sidebar groups routes into unordered CITY PAIRS (e.g. "Austin ↔
 * Houston" covers every route between those two cities, in both directions),
 * and a station's tooltip states what it is actually usable for rather than
 * asking the operator to pick a direction first. So the shape of this module
 * is: evaluate one station against one route (the primitive), then aggregate
 * that across every route in a pair (what the UI actually renders).
 *
 * No imports. This file must stay pure JavaScript over plain data so it can be
 * unit tested in complete isolation from React and from the map library.
 */

export const METERS_PER_MILE = 1609.344;

export const AVAILABILITY_KIND = {
  PICKUP: 'pickup',
  DROPOFF: 'dropoff',
};

export const STATION_PAIR_VERDICTS = {
  /** Usable for at least one end of at least one route in the pair. */
  ELIGIBLE: 'eligible',
  /** Within the corridor of at least one route, but qualifies for nothing —
   * not operational, or excluded at both ends. */
  IN_CORRIDOR_UNUSABLE: 'in-corridor-unusable',
  /** Beyond the corridor of every route in the pair. */
  OUTSIDE_CORRIDOR: 'outside-corridor',
};

/*
 * THERE IS NO ROUTE-TYPE GATE, and there must not be one.
 *
 * This module used to carry `PICKUP_ROUTE_TYPES` / `DROPOFF_ROUTE_TYPES`
 * membership checks on `station.routeType`. `station.route_type` no longer
 * exists: hitch-services#3466 deleted the production query's
 * `AND route_type = ANY(...)` after showing it to be a no-op, and #3469
 * dropped the column, the `route_type` enum, and the DAO projections that fed
 * this page. Mongo's `stationsV2` drew a real pick-up/drop-off distinction
 * (566 `pickUp` against 527 `pickUpAndDropOff`), but the postgres backfill
 * normalised all 975 rows to PICK_UP_AND_DROP_OFF and admin's create path
 * hardcoded it, so the gate could only ever be a no-op.
 *
 * Keeping the check after the field vanished was NOT a no-op, though: it read
 * `undefined`, and `[...].includes(undefined)` is `false`, so BOTH ends failed
 * for every station. In production that meant no station was ever ELIGIBLE, the
 * usable-station count was permanently 0, and every in-corridor station
 * rendered as IN_CORRIDOR_UNUSABLE hollow-orange — the precise "looks like your
 * limits rejected it" miscommunication `stationIcon` was written to avoid. It
 * went unnoticed because the fixtures in both Location test suites invented
 * the field, so every test agreed with the fixture rather than with the API.
 *
 * Removing it is what makes this module agree with production again, since the
 * real candidacy query no longer filters on route type either. If a
 * per-station pick-up/drop-off distinction is ever wanted back, it is a
 * backend change first — the value is recoverable only from mongo `stationsV2`
 * until that collection is dropped — and this module should be repointed at
 * the new field then, not at a resurrected constant.
 */

const hasFiniteRouteDistances = (route) =>
  Number.isFinite(route.distanceToPolylineMeters) &&
  Number.isFinite(route.distanceToOriginCityMeters) &&
  Number.isFinite(route.distanceToDestinationCityMeters);

/**
 * Per-route, per-end availability of one station against one route.
 *
 * `station` carries the station's own attributes that don't vary per route —
 * now just `isOperational` (see the route-type note above).
 * `route` carries this specific route's identity (`originCityName`,
 * `destinationCityName`) and this station's precomputed measurements against
 * it (`distanceToPolylineMeters`, `distanceToOriginCityMeters`,
 * `distanceToDestinationCityMeters`).
 *
 * A station qualifies as a pickup in the route's origin when it is within the
 * corridor, it is operational, and it is NOT within the far-city exclusion
 * radius of the DESTINATION. It qualifies as a dropoff in the destination
 * under the mirrored rule, excluded near the
 * ORIGIN instead. That far-city asymmetry — pickup excluded near the far end,
 * dropoff excluded near the near end — is the single easiest thing to get
 * backwards here.
 *
 * Every gate is a bare numeric comparison, which is unsafe against a missing
 * distance: `undefined > x` and `undefined < x` are both `false`, so a route
 * with an undefined distance would silently pass the corridor and far-city
 * gates and read as a confirmed candidate, while `null` (which coerces to `0`
 * in a `<` comparison) would give the opposite, contradictory answer for the
 * same missing datum. Guard against both up front rather than let either
 * reach the comparisons — a station the backend couldn't compute a distance
 * for must never read as usable.
 */
export const evaluateStationForRoute = (station, route, limits) => {
  if (!hasFiniteRouteDistances(route)) {
    return {
      isWithinCorridor: false,
      qualifiesForPickupInOrigin: false,
      qualifiesForDropoffInDestination: false,
    };
  }

  const isWithinCorridor = route.distanceToPolylineMeters <= limits.stationToPolylineMeters;

  const qualifiesForPickupInOrigin =
    isWithinCorridor &&
    station.isOperational &&
    !(route.distanceToDestinationCityMeters < limits.farCityExclusionMeters);

  const qualifiesForDropoffInDestination =
    isWithinCorridor &&
    station.isOperational &&
    !(route.distanceToOriginCityMeters < limits.farCityExclusionMeters);

  return { isWithinCorridor, qualifiesForPickupInOrigin, qualifiesForDropoffInDestination };
};

/**
 * Aggregated availability across a city pair's routes for one station.
 * `routes` is every route belonging to the pair, each carrying this
 * station's per-route measurements (see `evaluateStationForRoute`).
 *
 * Returns the distinct set of "pickup in <city>" / "dropoff in <city>"
 * statements as `{ kind, cityName }` — plain data, not pre-baked strings, so
 * the caller controls wording and ordering when rendering tooltip lines.
 * Deduplicated by `(kind, cityName)`, so near-duplicate routes between the
 * same two cities (e.g. six Austin→Houston routes) collapse to one entry
 * each rather than repeating.
 *
 * Because a city pair contains routes in both directions, the same station
 * can legitimately end up available for pickup in A (via an A→B route) AND
 * dropoff in A (via a B→A route) — both statements are kept.
 */
export const getStationAvailabilityForPair = (station, routes, limits) => {
  const byKey = new Map();

  routes.forEach((route) => {
    const { qualifiesForPickupInOrigin, qualifiesForDropoffInDestination } =
      evaluateStationForRoute(station, route, limits);

    if (qualifiesForPickupInOrigin) {
      const cityName = route.originCityName;
      byKey.set(`${AVAILABILITY_KIND.PICKUP}|${cityName}`, {
        kind: AVAILABILITY_KIND.PICKUP,
        cityName,
      });
    }
    if (qualifiesForDropoffInDestination) {
      const cityName = route.destinationCityName;
      byKey.set(`${AVAILABILITY_KIND.DROPOFF}|${cityName}`, {
        kind: AVAILABILITY_KIND.DROPOFF,
        cityName,
      });
    }
  });

  return Array.from(byKey.values());
};

/**
 * Three-state verdict for one station across a city pair's routes, used for
 * marker styling. Mutually exclusive and derivable for any station:
 *
 *   - ELIGIBLE: usable for at least one end of at least one route (i.e.
 *     `getStationAvailabilityForPair` returns something).
 *   - IN_CORRIDOR_UNUSABLE: within the corridor of at least one route, but
 *     qualifies for nothing (wrong route type, not operational, excluded at
 *     both ends, or some mix across the pair's routes).
 *   - OUTSIDE_CORRIDOR: beyond the corridor of every route in the pair
 *     (this also covers a station with no finite distance on any route,
 *     which must never be reported as eligible).
 */
export const getStationVerdictForPair = (station, routes, limits) => {
  if (getStationAvailabilityForPair(station, routes, limits).length > 0) {
    return STATION_PAIR_VERDICTS.ELIGIBLE;
  }

  const isWithinCorridorOfAnyRoute = routes.some(
    (route) => evaluateStationForRoute(station, route, limits).isWithinCorridor,
  );

  return isWithinCorridorOfAnyRoute
    ? STATION_PAIR_VERDICTS.IN_CORRIDOR_UNUSABLE
    : STATION_PAIR_VERDICTS.OUTSIDE_CORRIDOR;
};

/** How many stations are ELIGIBLE for this pair, for the sidebar/legend. */
export const countUsableStationsForPair = (stations, routes, limits) =>
  stations.filter(
    (station) =>
      getStationVerdictForPair(station, routes, limits) === STATION_PAIR_VERDICTS.ELIGIBLE,
  ).length;

/**
 * Mirrors resolveWithinSharedDoorRadius. Production first asks whether
 * EITHER door point is within the general along-route bound; only if so does
 * it go on to ask whether the shared radius should tighten that — and it only
 * tightens when the shared radius is actually smaller than the along-route
 * bound. When it is not tighter (the tightening flag is unset in production),
 * the check short-circuits and returns the OR result unchanged. Today the
 * live shared radius is 4,828 m (3 miles) against a 10,000 m along-route
 * bound, so the AND branch is the one that actually fires in production.
 *
 * DELIBERATELY UNCONSUMED — do not delete as dead code, and do not wire it to
 * whatever distances happen to be to hand. It needs an origin AND destination
 * door-distance pair for one prospective ride, and the lane map has no source
 * for that: it plots stations and lane geometry, not rider door points. (A
 * draggable rider pin was considered and rejected, because the map cannot know
 * which station a given rider would actually be offered.) Retained, and kept
 * under test, for a future view that does have rider door points — feeding it
 * two unrelated station distances would produce a confident wrong answer about
 * whether shared is offered.
 */
export const isWithinSharedDoorRadius = (
  originDistanceMeters,
  destinationDistanceMeters,
  limits,
) => {
  const isEitherAlongRoute =
    originDistanceMeters <= limits.alongRouteBoundMeters ||
    destinationDistanceMeters <= limits.alongRouteBoundMeters;

  if (!isEitherAlongRoute) return false;
  if (limits.sharedDoorRadiusMeters >= limits.alongRouteBoundMeters) {
    return isEitherAlongRoute;
  }

  return (
    originDistanceMeters <= limits.sharedDoorRadiusMeters &&
    destinationDistanceMeters <= limits.sharedDoorRadiusMeters
  );
};

/**
 * Every editable limit, in the order the panel shows them. The nearest-N cap
 * (`stationLimit`) and the fee-related limits (`alongRouteFeeCents`,
 * `minOffRouteFeeCents`, `costPerMileIndexCents`) are no longer editable here
 * — the nearest-N cap UI is retired product-side, and the fee readout is
 * being removed from this page.
 */
export const LIMIT_KEYS = [
  'stationToPolylineMeters',
  'farCityExclusionMeters',
  'sharedDoorRadiusMeters',
  'alongRouteBoundMeters',
];

/**
 * Limits as a query string, omitting anything still at its live value so a
 * shared URL only carries deliberate overrides.
 */
export const serializeLimits = (limits, liveLimits = {}) => {
  const params = new URLSearchParams();
  LIMIT_KEYS.forEach((key) => {
    const value = limits[key];
    if (value === undefined || value === null) return;
    if (liveLimits[key] === value) return;
    params.append(key, String(value));
  });
  return params.toString();
};

/**
 * Limits from a query string, falling back to the live value for anything
 * missing, unparseable, or negative — a bad URL must never produce a NaN
 * threshold that silently rejects every station and reads as "nothing is
 * eligible here", which is a dangerously plausible-looking wrong answer.
 */
export const deserializeLimits = (searchParams, liveLimits) => {
  const result = { ...liveLimits };
  LIMIT_KEYS.forEach((key) => {
    const raw = searchParams.get(key);
    if (raw === null || raw === '') return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    result[key] = parsed;
  });
  return result;
};

/**
 * Flatten the server's provenance-wrapped limits (GET /admin/lane-map/limits,
 * each field shaped `{ value, source }` with source one of 'constant' |
 * 'growthbook' | 'growthbook-experiment') into the flat shape the rest of this
 * module and the UI work with, keeping the sources alongside for labelling.
 *
 * The backend still sends `stationLimit` and `privatePricing` (they haven't
 * been removed server-side), but this module no longer treats them as
 * editable/serialisable limits, so they are deliberately not read here —
 * that keeps this function tolerant of the backend continuing to send them,
 * and equally tolerant if it later stops.
 *
 * doorToDoorSharedFeeCents is intentionally left out of `values` — it backs a
 * flag with four live production experiments, so there is no single number
 * that represents it honestly. Only its source is preserved, so a later task
 * can tell (via source === 'growthbook-experiment') to render it as a range
 * instead of one misleading value.
 *
 * laneMatchMeters and cityRadiusMeters aren't consumed by any eligibility
 * rule in this module, but they're part of the same live response and later
 * map-drawing tasks need them, so they're flattened through here too rather
 * than making every caller re-parse the provenance wrapper.
 */
export const flattenLiveLimits = (payload) => {
  if (!payload) return { values: null, sources: {} };
  return {
    values: {
      stationToPolylineMeters: payload.stationToPolylineMeters.value,
      farCityExclusionMeters: payload.farCityExclusionMeters.value,
      sharedDoorRadiusMeters: payload.sharedDoorRadiusMeters.value,
      alongRouteBoundMeters: payload.alongRouteBoundMeters.value,
      laneMatchMeters: payload.laneMatchMeters.value,
      cityRadiusMeters: payload.cityRadiusMeters.value,
    },
    sources: {
      stationToPolylineMeters: payload.stationToPolylineMeters.source,
      farCityExclusionMeters: payload.farCityExclusionMeters.source,
      sharedDoorRadiusMeters: payload.sharedDoorRadiusMeters.source,
      alongRouteBoundMeters: payload.alongRouteBoundMeters.source,
      doorToDoorSharedFeeCents: payload.doorToDoorSharedFeeCents.source,
      laneMatchMeters: payload.laneMatchMeters.source,
      cityRadiusMeters: payload.cityRadiusMeters.source,
    },
  };
};
